import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Users,
  Radio,
  Megaphone,
  ToggleLeft,
  DollarSign,
  Settings,
  Plus,
  Trash2,
  Edit,
  RefreshCw,
  Shield,
  ChevronLeft,
  TrendingUp,
  Crown,
  MessageSquare,
  Search,
  Check,
  X,
  Globe,
  MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin emails that can access this panel
const ADMIN_EMAILS = ['admin@veriwatch.io', 'intellitrexsystems@gmail.com'];

const AdminPanel = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [announcements, setAnnouncements] = useState([]);
  const [features, setFeatures] = useState([]);
  const [scanners, setScanners] = useState([]);
  const [users, setUsers] = useState([]);
  const [ticker, setTicker] = useState(null);
  const [tankQuotes, setTankQuotes] = useState([]);
  
  // Scanner Discovery states
  const [discoveryStats, setDiscoveryStats] = useState(null);
  const [pendingFeeds, setPendingFeeds] = useState([]);
  const [scanningState, setScanningState] = useState(null);
  const [selectedState, setSelectedState] = useState('CA');
  
  // Form states
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '', type: 'info', show_on: ['dashboard'] });
  const [newScanner, setNewScanner] = useState({ name: '', stream_url: '', state: '', city: '', type: 'police', latitude: 0, longitude: 0 });
  const [newFeature, setNewFeature] = useState({ key: '', enabled: false, description: '' });
  const [tickerForm, setTickerForm] = useState({ message: '', active: true, duration_seconds: 90, interval_minutes: 30 });
  const [newQuote, setNewQuote] = useState({ quote: '', mood: 'friendly' });
  
  // Check if user is admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);
  
  const fetchWithAuth = useCallback(async (endpoint) => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch');
    return res.json();
  }, [token]);
  
  const postWithAuth = useCallback(async (endpoint, data, method = 'POST') => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method,
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return res.json();
  }, [token]);
  
  // Load initial data
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    
    const loadData = async () => {
      try {
        const [statsData, announcementsData, featuresData, scannersData, usersData, tickerData, quotesData] = await Promise.all([
          fetchWithAuth('/api/admin/stats'),
          fetchWithAuth('/api/admin/announcements'),
          fetchWithAuth('/api/admin/features'),
          fetchWithAuth('/api/admin/scanners'),
          fetchWithAuth('/api/admin/users?limit=50'),
          fetchWithAuth('/api/admin/content/ticker'),
          fetchWithAuth('/api/admin/content/tank-quotes')
        ]);
        
        setStats(statsData);
        setAnnouncements(announcementsData.announcements || []);
        setFeatures(featuresData.features || []);
        setScanners(scannersData.scanners || []);
        setUsers(usersData.users || []);
        setTicker(tickerData.ticker);
        setTankQuotes(quotesData.quotes || []);
        
        if (tickerData.ticker) {
          setTickerForm(tickerData.ticker);
        }
        
        // Load discovery data
        try {
          const [discoveryStatsData, pendingData] = await Promise.all([
            fetchWithAuth('/api/admin/discovery/stats'),
            fetchWithAuth('/api/admin/discovery/pending?limit=50')
          ]);
          setDiscoveryStats(discoveryStatsData);
          setPendingFeeds(pendingData.feeds || []);
        } catch (err) {
          console.log('Discovery data not available yet');
        }
      } catch (err) {
        console.error('Failed to load admin data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [isAdmin, navigate, fetchWithAuth]);
  
  // Handlers
  const handleCreateAnnouncement = async () => {
    await postWithAuth('/api/admin/announcements', newAnnouncement);
    const data = await fetchWithAuth('/api/admin/announcements');
    setAnnouncements(data.announcements || []);
    setNewAnnouncement({ title: '', message: '', type: 'info', show_on: ['dashboard'] });
  };
  
  const handleDeleteAnnouncement = async (id) => {
    await postWithAuth(`/api/admin/announcements/${id}`, {}, 'DELETE');
    setAnnouncements(announcements.filter(a => a._id !== id));
  };
  
  const handleToggleFeature = async (key, enabled) => {
    await postWithAuth('/api/admin/features', { key, enabled });
    const data = await fetchWithAuth('/api/admin/features');
    setFeatures(data.features || []);
  };
  
  const handleCreateFeature = async () => {
    await postWithAuth('/api/admin/features', newFeature);
    const data = await fetchWithAuth('/api/admin/features');
    setFeatures(data.features || []);
    setNewFeature({ key: '', enabled: false, description: '' });
  };
  
  const handleCreateScanner = async () => {
    await postWithAuth('/api/admin/scanners', newScanner);
    const data = await fetchWithAuth('/api/admin/scanners');
    setScanners(data.scanners || []);
    setNewScanner({ name: '', stream_url: '', state: '', city: '', type: 'police', latitude: 0, longitude: 0 });
  };
  
  const handleToggleScanner = async (scannerId) => {
    await postWithAuth(`/api/admin/scanners/${scannerId}/toggle`, {});
    const data = await fetchWithAuth('/api/admin/scanners');
    setScanners(data.scanners || []);
  };
  
  const handleDeleteScanner = async (scannerId) => {
    await postWithAuth(`/api/admin/scanners/${scannerId}`, {}, 'DELETE');
    setScanners(scanners.filter(s => s.id !== scannerId));
  };
  
  const handleUpdateUserTier = async (userId, tier) => {
    await postWithAuth(`/api/admin/users/${userId}/tier`, { user_id: userId, tier }, 'PUT');
    const data = await fetchWithAuth('/api/admin/users?limit=50');
    setUsers(data.users || []);
  };
  
  const handleUpdateTicker = async () => {
    await postWithAuth('/api/admin/content/ticker', tickerForm, 'PUT');
    const data = await fetchWithAuth('/api/admin/content/ticker');
    setTicker(data.ticker);
  };
  
  const handleAddQuote = async () => {
    await postWithAuth('/api/admin/content/tank-quotes', newQuote);
    const data = await fetchWithAuth('/api/admin/content/tank-quotes');
    setTankQuotes(data.quotes || []);
    setNewQuote({ quote: '', mood: 'friendly' });
  };
  
  const handleDeleteQuote = async (id) => {
    await postWithAuth(`/api/admin/content/tank-quotes/${id}`, {}, 'DELETE');
    setTankQuotes(tankQuotes.filter(q => q._id !== id));
  };
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-slate-400">You don't have permission to access the admin panel.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-card border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-teal-500" />
              <h1 className="text-xl font-bold text-white">Admin Panel</h1>
            </div>
            <Badge className="bg-red-600 text-white">ADMIN</Badge>
          </div>
          <div className="text-sm text-slate-400">
            Logged in as: {user?.email}
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border border-slate-800 mb-8">
            <TabsTrigger value="overview" className="data-[state=active]:bg-teal-600">
              <TrendingUp className="w-4 h-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="announcements" className="data-[state=active]:bg-teal-600">
              <Megaphone className="w-4 h-4 mr-2" />Announcements
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-teal-600">
              <ToggleLeft className="w-4 h-4 mr-2" />Features
            </TabsTrigger>
            <TabsTrigger value="scanners" className="data-[state=active]:bg-teal-600">
              <Radio className="w-4 h-4 mr-2" />Scanners
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-teal-600">
              <Users className="w-4 h-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-teal-600">
              <MessageSquare className="w-4 h-4 mr-2" />Content
            </TabsTrigger>
            <TabsTrigger value="discovery" className="data-[state=active]:bg-teal-600">
              <Search className="w-4 h-4 mr-2" />Discovery
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400">Total Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats?.users?.total || 0}</div>
                  <p className="text-xs text-green-400">+{stats?.users?.recent_signups || 0} this week</p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400">Paid Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-teal-400">{stats?.users?.paid || 0}</div>
                  <p className="text-xs text-slate-500">{stats?.users?.free || 0} free users</p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400">Est. MRR</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-400">${stats?.revenue?.estimated_mrr || 0}</div>
                  <p className="text-xs text-slate-500">Monthly recurring</p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400">Active Scanners</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-amber-400">{stats?.scanners?.active || 0}</div>
                  <p className="text-xs text-slate-500">of {stats?.scanners?.total || 0} total</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Tier Breakdown */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Subscription Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 rounded-lg bg-slate-800">
                    <div className="text-2xl font-bold text-slate-400">{stats?.users?.free || 0}</div>
                    <div className="text-sm text-slate-500">Free</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-blue-950/50 border border-blue-800/50">
                    <div className="text-2xl font-bold text-blue-400">{stats?.users?.basic || 0}</div>
                    <div className="text-sm text-slate-500">Basic ($12)</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-teal-950/50 border border-teal-800/50">
                    <div className="text-2xl font-bold text-teal-400">{stats?.users?.pro || 0}</div>
                    <div className="text-sm text-slate-500">Pro ($16)</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-amber-950/50 border border-amber-800/50">
                    <div className="text-2xl font-bold text-amber-400">{stats?.users?.business || 0}</div>
                    <div className="text-sm text-slate-500">Business ($40)</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <Card className="bg-slate-900 border-slate-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Plus className="w-5 h-5" /> Create Announcement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input 
                  placeholder="Title"
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
                <Textarea 
                  placeholder="Message"
                  value={newAnnouncement.message}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, message: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
                <div className="flex gap-4">
                  <Select value={newAnnouncement.type} onValueChange={(v) => setNewAnnouncement({...newAnnouncement, type: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="alert">Alert</SelectItem>
                      <SelectItem value="promo">Promo</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleCreateAnnouncement} className="bg-teal-600 hover:bg-teal-500">
                    Create Announcement
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-4">
              {announcements.map((a) => (
                <Card key={a._id} className="bg-slate-900 border-slate-800">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white">{a.title}</span>
                        <Badge className={
                          a.type === 'alert' ? 'bg-red-600' :
                          a.type === 'warning' ? 'bg-amber-600' :
                          a.type === 'promo' ? 'bg-purple-600' : 'bg-blue-600'
                        }>{a.type}</Badge>
                        {a.active && <Badge className="bg-green-600">Active</Badge>}
                      </div>
                      <p className="text-sm text-slate-400">{a.message}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteAnnouncement(a._id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {announcements.length === 0 && (
                <p className="text-center text-slate-500 py-8">No announcements yet</p>
              )}
            </div>
          </TabsContent>
          
          {/* Features Tab */}
          <TabsContent value="features">
            <Card className="bg-slate-900 border-slate-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Plus className="w-5 h-5" /> Create Feature Flag
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Input 
                    placeholder="Feature key (e.g., new_dashboard)"
                    value={newFeature.key}
                    onChange={(e) => setNewFeature({...newFeature, key: e.target.value})}
                    className="bg-slate-800 border-slate-700 flex-1"
                  />
                  <Input 
                    placeholder="Description"
                    value={newFeature.description}
                    onChange={(e) => setNewFeature({...newFeature, description: e.target.value})}
                    className="bg-slate-800 border-slate-700 flex-1"
                  />
                  <Button onClick={handleCreateFeature} className="bg-teal-600 hover:bg-teal-500">
                    Create Flag
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-4">
              {features.map((f) => (
                <Card key={f._id} className="bg-slate-900 border-slate-800">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div>
                      <code className="text-teal-400 font-mono">{f.key}</code>
                      {f.description && <p className="text-sm text-slate-400 mt-1">{f.description}</p>}
                    </div>
                    <Switch 
                      checked={f.enabled} 
                      onCheckedChange={(checked) => handleToggleFeature(f.key, checked)}
                    />
                  </CardContent>
                </Card>
              ))}
              {features.length === 0 && (
                <p className="text-center text-slate-500 py-8">No feature flags yet</p>
              )}
            </div>
          </TabsContent>
          
          {/* Scanners Tab */}
          <TabsContent value="scanners">
            <Card className="bg-slate-900 border-slate-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Plus className="w-5 h-5" /> Add Scanner Feed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input 
                    placeholder="Name (e.g., LAPD Central)"
                    value={newScanner.name}
                    onChange={(e) => setNewScanner({...newScanner, name: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                  <Input 
                    placeholder="Stream URL"
                    value={newScanner.stream_url}
                    onChange={(e) => setNewScanner({...newScanner, stream_url: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                  <Input 
                    placeholder="State (e.g., CA)"
                    value={newScanner.state}
                    onChange={(e) => setNewScanner({...newScanner, state: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                  <Input 
                    placeholder="City"
                    value={newScanner.city}
                    onChange={(e) => setNewScanner({...newScanner, city: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                  <Input 
                    placeholder="Latitude"
                    type="number"
                    value={newScanner.latitude}
                    onChange={(e) => setNewScanner({...newScanner, latitude: parseFloat(e.target.value) || 0})}
                    className="bg-slate-800 border-slate-700"
                  />
                  <Input 
                    placeholder="Longitude"
                    type="number"
                    value={newScanner.longitude}
                    onChange={(e) => setNewScanner({...newScanner, longitude: parseFloat(e.target.value) || 0})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="flex gap-4">
                  <Select value={newScanner.type} onValueChange={(v) => setNewScanner({...newScanner, type: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="police">Police</SelectItem>
                      <SelectItem value="fire">Fire</SelectItem>
                      <SelectItem value="ems">EMS</SelectItem>
                      <SelectItem value="multi">Multi-Agency</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleCreateScanner} className="bg-teal-600 hover:bg-teal-500">
                    Add Scanner
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-2">
              <div className="text-sm text-slate-400 mb-2">
                {scanners.length} scanners total • {scanners.filter(s => s.active).length} active
              </div>
              {scanners.slice(0, 20).map((s) => (
                <Card key={s.id || s._id} className="bg-slate-900 border-slate-800">
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Switch 
                        checked={s.active} 
                        onCheckedChange={() => handleToggleScanner(s.id)}
                      />
                      <div>
                        <span className="font-medium text-white">{s.name}</span>
                        <span className="text-sm text-slate-500 ml-2">{s.city}, {s.state}</span>
                        <Badge className="ml-2 bg-slate-700">{s.type}</Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteScanner(s.id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">User Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {users.map((u) => (
                    <div key={u.id || u._id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800">
                      <div>
                        <span className="font-medium text-white">{u.name || u.email}</span>
                        <span className="text-sm text-slate-500 ml-2">{u.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={u.subscription?.tier || 'free'} 
                          onValueChange={(tier) => handleUpdateUserTier(u.id, tier)}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Content Tab */}
          <TabsContent value="content">
            {/* Ticker Settings */}
            <Card className="bg-slate-900 border-slate-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Bottom Ticker</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea 
                  placeholder="Ticker message"
                  value={tickerForm.message}
                  onChange={(e) => setTickerForm({...tickerForm, message: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Active:</span>
                    <Switch 
                      checked={tickerForm.active} 
                      onCheckedChange={(checked) => setTickerForm({...tickerForm, active: checked})}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Duration (sec):</span>
                    <Input 
                      type="number"
                      value={tickerForm.duration_seconds}
                      onChange={(e) => setTickerForm({...tickerForm, duration_seconds: parseInt(e.target.value) || 90})}
                      className="bg-slate-800 border-slate-700 w-20"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Interval (min):</span>
                    <Input 
                      type="number"
                      value={tickerForm.interval_minutes}
                      onChange={(e) => setTickerForm({...tickerForm, interval_minutes: parseInt(e.target.value) || 30})}
                      className="bg-slate-800 border-slate-700 w-20"
                    />
                  </div>
                  <Button onClick={handleUpdateTicker} className="bg-teal-600 hover:bg-teal-500">
                    Save Ticker
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Tank Quotes */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Tank's Quotes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Textarea 
                    placeholder="New quote for Tank..."
                    value={newQuote.quote}
                    onChange={(e) => setNewQuote({...newQuote, quote: e.target.value})}
                    className="bg-slate-800 border-slate-700 flex-1"
                  />
                  <div className="flex flex-col gap-2">
                    <Select value={newQuote.mood} onValueChange={(v) => setNewQuote({...newQuote, mood: v})}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="alert">Alert</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddQuote} className="bg-teal-600 hover:bg-teal-500">
                      Add Quote
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2 mt-4">
                  {tankQuotes.map((q) => (
                    <div key={q._id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800">
                      <div>
                        <p className="text-amber-200 italic">"{q.quote}"</p>
                        <Badge className="mt-1 bg-slate-700">{q.mood}</Badge>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteQuote(q._id)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Discovery Tab */}
          <TabsContent value="discovery">
            <div className="space-y-6">
              {/* Discovery Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-400">Total Discovered</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-white">{discoveryStats?.total_discovered || 0}</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-400">Pending Review</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-400">{discoveryStats?.pending_review || 0}</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-400">Approved</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-400">{discoveryStats?.approved || 0}</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-400">Rejected</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-red-400">{discoveryStats?.rejected || 0}</div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Scan Controls */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Globe className="w-5 h-5 text-teal-400" />
                    Scanner Discovery - Scan for New Sources
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <label className="text-sm text-slate-400 mb-2 block">Scan Single State</label>
                      <Select value={selectedState} onValueChange={setSelectedState}>
                        <SelectTrigger className="bg-slate-800 border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={async () => {
                        setScanningState(selectedState);
                        try {
                          await postWithAuth('/api/admin/discovery/scan/state', { state_code: selectedState });
                          // Poll for results
                          setTimeout(async () => {
                            const stats = await fetchWithAuth('/api/admin/discovery/stats');
                            setDiscoveryStats(stats);
                            const pending = await fetchWithAuth('/api/admin/discovery/pending');
                            setPendingFeeds(pending.feeds || []);
                            setScanningState(null);
                          }, 5000);
                        } catch (err) {
                          console.error(err);
                          setScanningState(null);
                        }
                      }}
                      disabled={scanningState !== null}
                      className="bg-teal-600 hover:bg-teal-500"
                    >
                      {scanningState === selectedState ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" />Scan {selectedState}</>
                      )}
                    </Button>
                    <Button 
                      onClick={async () => {
                        setScanningState('ALL');
                        try {
                          await postWithAuth('/api/admin/discovery/scan/all', { batch_size: 5 });
                          // This takes longer, poll periodically
                          const pollInterval = setInterval(async () => {
                            const stats = await fetchWithAuth('/api/admin/discovery/stats');
                            setDiscoveryStats(stats);
                            if (stats.total_discovered > (discoveryStats?.total_discovered || 0)) {
                              const pending = await fetchWithAuth('/api/admin/discovery/pending');
                              setPendingFeeds(pending.feeds || []);
                            }
                          }, 10000);
                          setTimeout(() => {
                            clearInterval(pollInterval);
                            setScanningState(null);
                          }, 300000); // 5 min timeout
                        } catch (err) {
                          console.error(err);
                          setScanningState(null);
                        }
                      }}
                      disabled={scanningState !== null}
                      variant="outline"
                      className="border-amber-600 text-amber-400 hover:bg-amber-600/20"
                    >
                      {scanningState === 'ALL' ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning Nation...</>
                      ) : (
                        <><Globe className="w-4 h-4 mr-2" />Scan All 50 States</>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Scans OpenMHz, Icecast, and direct agency websites for FREE scanner feeds. Single state takes ~5 seconds, full nation takes ~5 minutes.
                  </p>
                </CardContent>
              </Card>
              
              {/* Pending Feeds for Review */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white">Pending Review ({pendingFeeds.length})</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={async () => {
                      const pending = await fetchWithAuth('/api/admin/discovery/pending?limit=100');
                      setPendingFeeds(pending.feeds || []);
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {pendingFeeds.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No feeds pending review. Run a scan to discover new sources.</p>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {pendingFeeds.map((feed) => (
                        <div key={feed._id} className="flex items-center justify-between p-4 rounded-lg bg-slate-800 border border-slate-700">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-white">{feed.name}</span>
                              <Badge className="bg-blue-600">{feed.source}</Badge>
                              <Badge className="bg-slate-600">{feed.state}</Badge>
                            </div>
                            {feed.location && (
                              <p className="text-sm text-slate-400 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />{feed.location}
                              </p>
                            )}
                            {feed.stream_url && (
                              <p className="text-xs text-slate-500 truncate max-w-md">{feed.stream_url}</p>
                            )}
                            {feed.validation && (
                              <Badge className={feed.validation.valid ? 'bg-green-600 mt-1' : 'bg-red-600 mt-1'}>
                                {feed.validation.valid ? 'Stream Valid' : 'Stream Invalid'}
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-500"
                              onClick={async () => {
                                await postWithAuth('/api/admin/discovery/approve', { feed_id: feed._id });
                                setPendingFeeds(pendingFeeds.filter(f => f._id !== feed._id));
                                const stats = await fetchWithAuth('/api/admin/discovery/stats');
                                setDiscoveryStats(stats);
                              }}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-600 text-red-400 hover:bg-red-600/20"
                              onClick={async () => {
                                await postWithAuth('/api/admin/discovery/reject', { feed_id: feed._id, reason: 'Rejected by admin' });
                                setPendingFeeds(pendingFeeds.filter(f => f._id !== feed._id));
                                const stats = await fetchWithAuth('/api/admin/discovery/stats');
                                setDiscoveryStats(stats);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPanel;
